import React, { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
  Card, CardBody, CardHeader, Button, ButtonGroup,
  Row, Col, Container, Spinner, Form, FormGroup, Label, Input,
  Table
} from 'reactstrap';
import { format, parseISO, formatISO, parse } from 'date-fns';
import { fr } from 'date-fns/locale';
import { saleActions } from '../../sagas/saleSlice';
import { FaCalendarAlt, FaDownload, FaUser, FaFileAlt, FaPrint } from 'react-icons/fa';
import { convertNumberToWords, formatPrice } from '../../helpers/utility';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import Select from "react-select";
import autoTable from 'jspdf-autotable';
import { supplierActions } from '../../sagas/supplierSlice';
import { purchaseActions } from '../../sagas/purchaseSlice';

const AchatsStatistiques = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { company } = useSelector(state => state.auth);
  const [brFilter, setBrFilter] = useState('');
  const [designationFilter, setDesignationFilter] = useState('');

  useEffect(() => {
    dispatch(purchaseActions.clearSupplierReport());
  }, []);

  const parseDate = (dateString) => {
    if (!dateString) return new Date();
    if (dateString.includes('-')) {
      return parseISO(dateString);
    }
    return parse(dateString, 'dd/MM/yyyy', new Date());
  };

  // State
  const [dateRange, setDateRange] = useState('month');
  const [customDateRange, setCustomDateRange] = useState({
    startDate: format(new Date(), 'yyyy-MM-dd'),
    endDate: format(new Date(Date.now() + 24 * 60 * 60 * 1000), 'yyyy-MM-dd')
  });

  // Supplier report state
  const { suppliers, loading: suppliersLoading } = useSelector(state => state.suppliers);
  const { supplierReport, supplierReportLoading, supplierReportError } = useSelector(state => state.purchases);
  const [selectedSupplier, setSelectedSupplier] = useState('');
  const supplierReportRef = useRef(null);

  const fournisseurOptions = suppliers?.map(sup => ({
        solde: sup.solde,
        totalDebit: sup.totalDebit,
        totalCredit: sup.totalCredit,
        value: sup.id,
        label: sup.typeFournisseur === 'PARTICULIER' ? `${sup.nom} ${sup.prenom}` : sup.entreprise,
    })) ?? [];

  // Effect for fetching data
  useEffect(() => {
    if (suppliers.length === 0) {
      // Fetch suppliers for supplier report
      const fetchSuppliers = async () => {
        dispatch(supplierActions.search({
          query: ""
        }));
      };

      fetchSuppliers();
    }

    dispatch(supplierActions.search({
      query: ""
    }));

  }, []);

  const handleCustomDateChange = (e) => {
    const { name, value } = e.target;
    setCustomDateRange(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Handle supplier selection
  const handleSupplierChange = (value) => {
    setSelectedSupplier(value);
  };

  // Generate supplier report
  const generateSupplierReport = () => {
    if (!selectedSupplier) return;

    dispatch(purchaseActions.generateSupplierReport({
      supplierId: selectedSupplier,
      dateDebut: customDateRange.startDate,
      dateFin: customDateRange.endDate
    }));
  };

  const exportSupplierReportToPDF = () => {
    if (!supplierReport) return;

    // Create PDF in landscape mode
    const doc = new jsPDF({
      orientation: 'landscape',
      unit: 'mm',
      format: 'a4'
    });

    // Page dimensions
    const pageWidth = doc.internal.pageSize.width;
    const pageHeight = doc.internal.pageSize.height;
    const margin = 10;

    // Define the footer height to reserve space at bottom of each page
    const footerHeight = 15;

    // Enhanced color scheme
    const primaryColor = [41, 128, 185]; // Blue
    const secondaryColor = [52, 152, 219]; // Lighter blue
    const accentColor = [39, 174, 96]; // Green for positive values
    const negativeColor = [231, 76, 60]; // Red for negative values
    const lightGray = [245, 245, 245]; // Background for alternating rows

    // Function to add header to each page
    const addPageHeader = () => {
      // Header with gradient-like effect
      doc.setFillColor(primaryColor[0], primaryColor[1], primaryColor[2]);
      doc.rect(0, 0, pageWidth, 30, 'F');
      doc.setFillColor(secondaryColor[0], secondaryColor[1], secondaryColor[2]);
      doc.rect(0, 25, pageWidth, 5, 'F');

      // Document title with improved typography
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(22);
      doc.setFont('helvetica', 'bold');
      doc.text('RAPPORT ACHAT FOURNISSEUR', pageWidth / 2, 15, { align: 'center' });

      // Period with better spacing
      doc.setFontSize(11);
      doc.setFont('helvetica', 'normal');
      const formattedDateFrom = format(parseDate(supplierReport.period.from), 'dd/MM/yyyy', { locale: fr });
      const formattedDateTo = format(parseDate(supplierReport.period.to), 'dd/MM/yyyy', { locale: fr });
      doc.text(`Période: du ${formattedDateFrom} au ${formattedDateTo}`, pageWidth / 2, 22, { align: 'center' });
    };

    // Function to add footer to each page
    const addPageFooter = () => {
      const footerY = pageHeight - footerHeight;
      doc.setDrawColor(200, 200, 200);
      doc.setLineWidth(0.3);
      doc.line(margin, footerY, pageWidth - margin, footerY);

      doc.setFontSize(8);
      doc.setTextColor(100, 100, 100);
      doc.text(`Document généré le: ${supplierReport.generationDate}`, pageWidth - margin, footerY + 5, { align: 'right' });
      doc.text(company.name, margin, footerY + 5);
      doc.text(`Page ${doc.internal.getNumberOfPages()}`, pageWidth / 2, footerY + 5, { align: 'center' });
    };

    // Function to format amount with sign and color
    const formatAmountWithSign = (amount) => {
      const numAmount = parseFloat(amount);
      if (isNaN(numAmount)) return formatPrice(0, false);

      if (numAmount > 0) {
        return `+${formatPrice(numAmount, false)}`;
      } else if (numAmount < 0) {
        // For negative values, we want to show the minus sign
        // but formatPrice already includes it for negative numbers
        return formatPrice(numAmount, false);
      } else {
        return formatPrice(0, false);
      }
    };

    // Add first page header
    addPageHeader();

    // Company information with logo placeholder
    doc.setTextColor(0, 0, 0);
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.text(company.name, margin, 40);

    // Add a decorative line under company name
    doc.setDrawColor(primaryColor[0], primaryColor[1], primaryColor[2]);
    doc.setLineWidth(0.5);
    doc.line(margin, 42, margin + 60, 42);

    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    doc.text(`Adresse: ${company.address}`, margin, 47);
    doc.text(`Tél: ${company.phone1} | Email: ${company.email}`, margin, 52);
    doc.text(`RC: ${company.rc} | IF: ${company.identificationFiscale} | ICE: ${company.ice}`, margin, 57);

    // Client information in a styled box
    doc.setFillColor(248, 249, 250);
    doc.setDrawColor(220, 220, 220);
    doc.roundedRect(pageWidth - margin - 80, 35, 80, 30, 2, 2, 'FD');

    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.text('INFORMATIONS FOURNISSEUR', pageWidth - margin - 75, 42);

    // Add a decorative line under supplier info title
    doc.setDrawColor(accentColor[0], accentColor[1], accentColor[2]);
    doc.line(pageWidth - margin - 75, 44, pageWidth - margin - 15, 44);

    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    doc.text(`Fournisseur: ${supplierReport.supplier.name}`, pageWidth - margin - 75, 50);
    doc.text(`Téléphone: ${supplierReport.supplier.phone}`, pageWidth - margin - 75, 55);
    doc.text(`Code fournisseur: ${supplierReport.supplier.code}`, pageWidth - margin - 75, 60);

    // Delivery notes table data preparation
    const tableHeaders = ['N° BR', 'Date', 'Désignation', 'Quantité', 'UM', 'PU', 'Total'];

    const tableData = supplierReport.bonsReception.map(note => [
      note.brNumber,
      format(parseDate(note.date), 'dd/MM/yyyy', { locale: fr }),
      note.designation,
      note.quantity.toString(),
      note.unit,
      formatPrice(note.unitPrice, false),
      formatPrice(note.lineTotal, false)
    ]);

    // Calculate column widths for full-width table
    const tableWidth = pageWidth - (2 * margin);
    const colWidth1 = tableWidth * 0.1; // N° BL
    const colWidth2 = tableWidth * 0.1;  // Date
    const colWidth3 = tableWidth * 0.38;  // Désignation (largest column)
    const colWidth4 = tableWidth * 0.08; // Quantité
    const colWidth5 = tableWidth * 0.08; // UM
    const colWidth6 = tableWidth * 0.13; // PU
    const colWidth7 = tableWidth * 0.13; // Total

    // Format dates for summary
    const formattedDateFrom = format(parseDate(supplierReport.period.from), 'dd/MM/yyyy', { locale: fr });
    const formattedDateTo = format(parseDate(supplierReport.period.to), 'dd/MM/yyyy', { locale: fr });

    // Pagination logic
    const totalRows = tableData.length;
    let currentRow = 0;

    // First page - always 8 rows
    const firstPageRows = Math.min(8, totalRows);
    const firstPageData = tableData.slice(0, firstPageRows);
    currentRow += firstPageRows;

    // Add first page table
    autoTable(doc, {
      startY: 70,
      head: [tableHeaders],
      body: firstPageData,
      theme: 'grid',
      headStyles: {
        fillColor: primaryColor,
        textColor: [255, 255, 255],
        fontStyle: 'bold',
        halign: 'center',
        fontSize: 10
      },
      columnStyles: {
        0: { halign: 'center', cellWidth: colWidth1 },
        1: { halign: 'center', cellWidth: colWidth2 },
        2: { halign: 'left', cellWidth: colWidth3 },
        3: { halign: 'center', cellWidth: colWidth4 },
        4: { halign: 'center', cellWidth: colWidth5 },
        5: { halign: 'right', cellWidth: colWidth6 },
        6: { halign: 'right', cellWidth: colWidth7 }
      },
      alternateRowStyles: {
        fillColor: lightGray
      },
      styles: {
        overflow: 'linebreak',
        cellPadding: 3,
        fontSize: 9
      },
      margin: { top: margin, right: margin, bottom: footerHeight, left: margin },
      didDrawPage: () => {
        addPageFooter();
      }
    });

    // Calculate remaining rows
    const remainingRows = totalRows - currentRow;

    // If we have more rows to display
    if (remainingRows > 0) {
      // Calculate how many middle pages with 12 rows each
      const middlePageCount = Math.floor(remainingRows / 12);

      // Add middle pages with 12 rows each
      for (let i = 0; i < middlePageCount; i++) {
        doc.addPage();
        addPageHeader();

        const pageData = tableData.slice(currentRow, currentRow + 12);
        currentRow += 12;

        autoTable(doc, {
          startY: 40, // Start after header
          head: [tableHeaders],
          body: pageData,
          theme: 'grid',
          headStyles: {
            fillColor: primaryColor,
            textColor: [255, 255, 255],
            fontStyle: 'bold',
            halign: 'center',
            fontSize: 10
          },
          columnStyles: {
            0: { halign: 'center', cellWidth: colWidth1 },
            1: { halign: 'center', cellWidth: colWidth2 },
            2: { halign: 'left', cellWidth: colWidth3 },
            3: { halign: 'center', cellWidth: colWidth4 },
            4: { halign: 'center', cellWidth: colWidth5 },
            5: { halign: 'right', cellWidth: colWidth6 },
            6: { halign: 'right', cellWidth: colWidth7 }
          },
          alternateRowStyles: {
            fillColor: lightGray
          },
          styles: {
            overflow: 'linebreak',
            cellPadding: 3,
            fontSize: 9
          },
          margin: { top: margin, right: margin, bottom: footerHeight, left: margin },
          didDrawPage: () => {
            addPageFooter();
          }
        });
      }

      // Check if we have any remaining rows for the last page
      const lastPageRows = remainingRows % 12;

      if (lastPageRows > 0) {
        doc.addPage();
        addPageHeader();

        const lastPageData = tableData.slice(currentRow);

        autoTable(doc, {
          startY: 40, // Start after header
          head: [tableHeaders],
          body: lastPageData,
          foot: [['', '', '', '', '', 'Total:', formatPrice(supplierReport.summary.totalConsome, false)]],
          theme: 'grid',
          headStyles: {
            fillColor: primaryColor,
            textColor: [255, 255, 255],
            fontStyle: 'bold',
            halign: 'center',
            fontSize: 10
          },
          footStyles: {
            fillColor: [240, 240, 240],
            textColor: [0, 0, 0],
            fontStyle: 'bold',
            fontSize: 10
          },
          columnStyles: {
            0: { halign: 'center', cellWidth: colWidth1 },
            1: { halign: 'center', cellWidth: colWidth2 },
            2: { halign: 'left', cellWidth: colWidth3 },
            3: { halign: 'center', cellWidth: colWidth4 },
            4: { halign: 'center', cellWidth: colWidth5 },
            5: { halign: 'right', cellWidth: colWidth6 },
            6: { halign: 'right', cellWidth: colWidth7 }
          },
          alternateRowStyles: {
            fillColor: lightGray
          },
          styles: {
            overflow: 'linebreak',
            cellPadding: 3,
            fontSize: 9
          },
          margin: { top: margin, right: margin, bottom: footerHeight, left: margin },
          didDrawPage: () => {
            addPageFooter();
          }
        });
      } else {
        // If we don't have any rows for the last page (exactly divisible by 12),
        // add the total row to the last table we created
        doc.setFontSize(10);
        doc.setFont('helvetica', 'bold');
        doc.text('Total:', pageWidth - margin - colWidth7 - 5, doc.lastAutoTable.finalY + 8, { align: 'right' });
        doc.text(formatPrice(supplierReport.summary.totalConsome, false), pageWidth - margin, doc.lastAutoTable.finalY + 8, { align: 'right' });
      }
    } else {
      // If all data fits on the first page, add the total row
      doc.setFontSize(10);
      doc.setFont('helvetica', 'bold');
      doc.text('Total:', pageWidth - margin - colWidth7 - 5, doc.lastAutoTable.finalY + 8, { align: 'right' });
      doc.text(formatPrice(supplierReport.summary.totalConsome, false), pageWidth - margin, doc.lastAutoTable.finalY + 8, { align: 'right' });
    }

    // Get the Y position after the table
    let summaryY = doc.lastAutoTable.finalY + 15;

    // Check if we have enough space for summary section (at least 70mm)
    // If not, add a new page
    if (summaryY > pageHeight - 80) {
      doc.addPage();
      addPageHeader();
      summaryY = 40; // Start after header on new page
    }

    // Summary section with styled box - left side
    doc.setFillColor(248, 249, 250);
    doc.roundedRect(margin, summaryY, pageWidth * 0.3, 50, 2, 2, 'F');

    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(primaryColor[0], primaryColor[1], primaryColor[2]);
    doc.text('RÉSUMÉ', margin + 5, summaryY + 8);

    // Add a decorative line under summary title
    doc.setDrawColor(primaryColor[0], primaryColor[1], primaryColor[2]);
    doc.line(margin + 5, summaryY + 10, margin + 35, summaryY + 10);

    doc.setTextColor(0, 0, 0);

    // Prepare data for summary table
    // We'll handle the Net A Payer row separately for color formatting
    const summaryData = [
      ['Montant Total Consommé', formatPrice(supplierReport.summary.totalConsome, false)],
      [`Solde Avant le ${formattedDateFrom}`, formatPrice(supplierReport.summary.soldeAvant, false)],
      ['Avance', formatPrice(supplierReport.summary.avance, false)],
      ['Avoir', formatPrice(supplierReport.summary.avoir, false)]
    ];

    // Summary table with better styling (without Net A Payer row)
    autoTable(doc, {
      startY: summaryY + 15,
      body: summaryData,
      theme: 'plain',
      styles: {
        cellPadding: 3,
        fontSize: 10
      },
      columnStyles: {
        0: { fontStyle: 'bold', cellWidth: pageWidth * 0.2 },
        1: { halign: 'right', cellWidth: pageWidth * 0.1 }
      },
      tableWidth: pageWidth * 0.3
    });

    // Get the Y position after the summary table
    const netAPayerY = doc.lastAutoTable.finalY + 5;

    // Add the Net A Payer row with color based on value
    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.text('Net A Payer', margin + 5, netAPayerY);

    // Determine color and format for Net A Payer amount
    const netAPayerAmount = parseFloat(supplierReport.summary.netApaye);
    if (netAPayerAmount > 0) {
      // Positive amount - green with + sign
      doc.setTextColor(accentColor[0], accentColor[1], accentColor[2]);
      doc.text(`+${formatPrice(netAPayerAmount, false)}`, margin + pageWidth * 0.3 - 5, netAPayerY, { align: 'right' });
    } else if (netAPayerAmount < 0) {
      // Negative amount - red (minus sign already included in formatPrice)
      doc.setTextColor(negativeColor[0], negativeColor[1], negativeColor[2]);
      doc.text(formatPrice(netAPayerAmount, false), margin + pageWidth * 0.3 - 5, netAPayerY, { align: 'right' });
    } else {
      // Zero amount - neutral color
      doc.setTextColor(0, 0, 0);
      doc.text(formatPrice(0, false), margin + pageWidth * 0.3 - 5, netAPayerY, { align: 'right' });
    }

    // Reset text color for subsequent content
    doc.setTextColor(0, 0, 0);

    // Amount in words in a styled box - right side
    const amountY = summaryY;
    doc.setFillColor(255, 255, 255);
    doc.setDrawColor(accentColor[0], accentColor[1], accentColor[2]);
    doc.roundedRect(pageWidth - margin - (pageWidth * 0.4), amountY, pageWidth * 0.4, 40, 2, 2, 'FD');

    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(accentColor[0], accentColor[1], accentColor[2]);
    doc.text('Arrêté le présent document à la somme de:', pageWidth - margin - (pageWidth * 0.38), amountY + 10);

    // Set color for amount in words based on value
    if (netAPayerAmount < 0) {
      doc.setTextColor(negativeColor[0], negativeColor[1], negativeColor[2]);
    } else {
      doc.setTextColor(accentColor[0], accentColor[1], accentColor[2]);
    }

    doc.setFont('helvetica', 'italic');
    // Use absolute value for amount in words, but indicate negative if needed
    const amountWords = netAPayerAmount < 0
      ? `Moins ${convertNumberToWords(Math.abs(netAPayerAmount))}`
      : convertNumberToWords(netAPayerAmount);
    doc.text(`${amountWords} Dirhams`,
      pageWidth - margin - (pageWidth * 0.38), amountY + 20,
      { maxWidth: pageWidth * 0.36 });

    // Note with a subtle background - positioned below amount in words
    doc.setFillColor(245, 245, 245);
    doc.roundedRect(pageWidth - margin - (pageWidth * 0.4), amountY + 45, pageWidth * 0.4, 15, 2, 2, 'F');

    doc.setFontSize(8);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(100, 100, 100);
    doc.text(`NB: Montant Total Consommé sur la période du ${formattedDateFrom} au ${formattedDateTo}`,
      pageWidth - margin - (pageWidth * 0.38), amountY + 52,
      { maxWidth: pageWidth * 0.36 });

    // Add footer to the last page
    addPageFooter();

    // Save the PDF with a well-formatted filename
    const sanitizedSupplierName = supplierReport.supplier.name.replace(/[^a-z0-9]/gi, '_').toLowerCase();
    doc.save(`rapport_achat_${sanitizedSupplierName}_${format(new Date(), 'yyyyMMdd')}.pdf`);
  };


  // Filter states for BR number and designation


  // Filtered delivery notes based on BR number and designation
  let filteredDeliveryNotes = [];
  if (supplierReport && supplierReport.bonsReception) {
    filteredDeliveryNotes = supplierReport.bonsReception.filter(note => {
      const brMatch = brFilter ? (note.brNumber || '').toUpperCase().includes(brFilter) : true;
      const designationMatch = designationFilter ? (note.designation || '').toUpperCase().includes(designationFilter) : true;
      return brMatch && designationMatch;
    });
  }

  return (
    <Container fluid className="page-content">
      <Card className="shadow-sm mb-4">
        <CardHeader className="bg-white">
          <div className="d-flex justify-content-between align-items-center">
            <h5 className="mb-0">
              <FaFileAlt className="me-2 text-primary" />
              Détails des ventes
            </h5>
          </div>
        </CardHeader>
        <CardBody>
          <Row className="mb-4">
            <Col md={3}>
              <FormGroup>
                <Label for="supplierSelect" className="form-label">
                  <FaUser className="me-1" /> Fournisseur
                </Label>
                <Select
                  id="supplierSelect"
                  options={fournisseurOptions}
                  isLoading={suppliersLoading}
                  onChange={(option) => handleSupplierChange(option?.value)}
                  placeholder="Sélectionner..."
                  isClearable
                  className="react-select-container"
                  classNamePrefix="react-select"
                  menuPortalTarget={typeof window !== 'undefined' ? document.body : null}
                  styles={{
                    menuPortal: base => ({ ...base, zIndex: 9999 })
                  }}
                />
              </FormGroup>
            </Col>
            <Col md={3}>
              <FormGroup>
                <Label for="startDate" className="form-label">
                  <FaCalendarAlt className="me-1" /> Date de début
                </Label>
                <Input
                  type="date"
                  id="startDate"
                  name="startDate"
                  value={customDateRange.startDate}
                  onChange={handleCustomDateChange}
                />
              </FormGroup>
            </Col>
            <Col md={3}>
              <FormGroup>
                <Label for="endDate" className="form-label">
                  <FaCalendarAlt className="me-1" /> Date de fin
                </Label>
                <Input
                  type="date"
                  id="endDate"
                  name="endDate"
                  value={customDateRange.endDate}
                  onChange={handleCustomDateChange}
                />
              </FormGroup>
            </Col>
            <Col md={3} className="d-flex mb-3 align-items-end">
              <Button
                color="success"
                className="me-2"
                onClick={generateSupplierReport}
                disabled={!selectedSupplier || supplierReportLoading}
              >
                {supplierReportLoading ? (
                  <>
                    <Spinner size="sm" className="me-1" /> Génération...
                  </>
                ) : (
                  <>
                    <FaFileAlt className="me-1" /> Générer
                  </>
                )}
              </Button>
              {supplierReport && (
                <>
                  <Button
                    color="secondary"
                    onClick={exportSupplierReportToPDF}
                  >
                    <FaDownload className="me-1" /> Télécharger
                  </Button>
                </>
              )}
            </Col>
          </Row>

          {/* Supplier Report Content */}
          <div ref={supplierReportRef}>
            {supplierReport ? (
              <div className="client-report p-4 border rounded-4 bg-white shadow-sm animate__animated animate__fadeIn">
                {/* <div className="report-header mb-4 pb-2 border-bottom">
                  <div className="text-center mb-3">
                    <h4 className="text-primary mb-1 fw-bold letter-spacing-1">RAPPORT VENTE CLIENT</h4>
                    <p className="text-muted small mb-0">
                      <FaCalendarAlt className="me-1" />
                      Période: <span className="fw-semibold">du {format(parseDate(supplierReport.period.from), 'dd/MM/yyyy', { locale: fr })} au {format(parseDate(supplierReport.period.to), 'dd/MM/yyyy', { locale: fr })}</span>
                    </p>
                  </div>

                  <Row className="mb-3 g-4">
                    <Col md={6}>
                      <div className="company-info bg-light p-3 rounded-3 h-100">
                        <h5 className="mb-2 text-dark fw-bold"><FaFileAlt className="me-2 text-primary" />{company.name}</h5>
                        <div className="small text-secondary">
                          <div><span className="fw-semibold">Adresse:</span> {company.address}</div>
                          <div><span className="fw-semibold">Tél:</span> {company.phone1} <span className="mx-1">|</span> <span className="fw-semibold">Email:</span> {company.email}</div>
                          <div><span className="fw-semibold">RC:</span> {company.rc} <span className="mx-1">|</span> <span className="fw-semibold">IF:</span> {company.identificationFiscale} <span className="mx-1">|</span> <span className="fw-semibold">ICE:</span> {company.ice}</div>
                        </div>
                      </div>
                    </Col>
                    <Col md={6}>
                      <div className="client-info bg-white border border-2 border-primary p-3 rounded-3 h-100">
                        <h6 className="mb-2 text-primary fw-bold"><FaUser className="me-2" />Informations Client</h6>
                        <div className="small">
                          <div className="mb-1"><strong>Client:</strong> {supplierReport.client.name}</div>
                          <div className="mb-1"><strong>Téléphone:</strong> {supplierReport.client.phone}</div>
                          <div className="mb-0"><strong>Code client:</strong> {supplierReport.client.code}</div>
                        </div>
                      </div>
                    </Col>
                  </Row>
                </div> */}

                <div className="report-body mb-4">
                  <div className="table-responsive rounded-3 overflow-hidden">
                    {/* Filter fields for BL number and product designation */}
                    <div className="d-flex gap-3 mb-2">
                      <Input
                        type="text"
                        placeholder="Filtrer par N° BR..."
                        value={brFilter || ''}
                        onChange={e => setBrFilter(e.target.value.toUpperCase())}
                        className="border border-2 rounded-3 px-3 text-uppercase fw-bold"
                        style={{ maxWidth: 180 }}
                      />
                      <Input
                        type="text"
                        placeholder="Filtrer par Désignation..."
                        value={designationFilter || ''}
                        onChange={e => setDesignationFilter(e.target.value.toUpperCase())}
                        className="border border-2 rounded-3 px-3 text-uppercase fw-bold"
                        style={{ maxWidth: 260 }}
                      />
                    </div>
                    <Table bordered hover size="sm" className="mb-0 align-middle">
                      <thead className="table-primary">
                        <tr>
                          <th className="text-center">N° BL</th>
                          <th className="text-center">Date</th>
                          <th>Désignation</th>
                          <th className="text-center">Quantité</th>
                          <th className="text-center">UM</th>
                          <th className="text-end">PU</th>
                          <th className="text-end">Total</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredDeliveryNotes.map((note, index) => (
                          <tr key={note.id} className={index % 2 === 0 ? "bg-light" : ""}>
                            <td className="text-center fw-semibold">{note.brNumber}</td>
                            <td className="text-center">{format(parseDate(note.date), 'dd/MM/yyyy', { locale: fr })}</td>
                            <td className="ps-2">{note.designation}</td>
                            <td className="text-center">{note.quantity}</td>
                            <td className="text-center">{note.unit}</td>
                            <td className="text-end">{formatPrice(note.unitPrice, false)}</td>
                            <td className="text-end fw-bold">{formatPrice(note.lineTotal, false)}</td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot className="table-light">
                        <tr>
                          <td colSpan="3" className="text-end fw-bold">Total:</td>
                          <td className="text-center fw-bold text-primary">
                            {filteredDeliveryNotes.reduce((acc, note) => acc + (Number(note.quantity) || 0), 0)}
                          </td>
                          <td></td>
                          <td></td>
                          <td className="text-end fw-bold text-primary">{formatPrice(filteredDeliveryNotes.reduce((acc, note) => acc + (note.lineTotal || 0), 0), false)}</td>
                        </tr>
                      </tfoot>
                    </Table>

                  </div>
                </div>

                <div className="report-summary mb-4">
                  <Row className="g-4">
                    <Col md={6}>
                      <div className="mb-3 bg-light p-3 rounded-3 border border-1 border-secondary">
                        <h6 className="border-bottom pb-2 mb-3 text-secondary fw-bold">Résumé</h6>
                        <Table borderless size="sm" className="summary-table">
                          <tbody>
                            <tr>
                              <td className="fw-bold">Montant Total Consommé</td>
                              <td className="text-end">{formatPrice(supplierReport.summary.totalConsome || 0, false)}</td>
                            </tr>
                            <tr>
                              <td className="fw-bold">Solde Avant le {format(parseDate(supplierReport.period.from), 'dd/MM/yyyy', { locale: fr })}</td>
                              <td className="text-end">{formatPrice(supplierReport.summary.soldeAvant || 0, false)}</td>
                            </tr>
                            <tr>
                              <td className="fw-bold">Avance</td>
                              <td className="text-end">{formatPrice(supplierReport.summary.avance || 0, false)}</td>
                            </tr>
                            <tr>
                              <td className="fw-bold">Règlements normaux (hors avances)</td>
                              <td className="text-end">{formatPrice(supplierReport.summary.reglements || 0, false)}</td>
                            </tr>
                            <tr>
                              <td className="fw-bold">Avoir</td>
                              <td className="text-end">{formatPrice(supplierReport.summary.avoir || 0, false)}</td>
                            </tr>
                            <tr className="table-light">
                              <td className="fw-bold">Net A Payer</td>
                              {
                                supplierReport.summary.netApaye < 0 ? (
                                  <td className="text-end fw-bold text-danger">{formatPrice(supplierReport.summary.netApaye, false)}</td>
                                ) : (
                                  <td className="text-end fw-bold text-success">{formatPrice(supplierReport.summary.netApaye, false)}</td>
                                )
                              }
                            </tr>
                          </tbody>
                        </Table>
                      </div>
                    </Col>
                    <Col md={6}>
                      <div className="amount-in-words p-4 bg-white rounded-3 border border-1 border-primary">
                        <p className="mb-1 fw-bold text-primary">Arrêté le présent document à la somme de:</p>
                        {
                          supplierReport.summary.netApaye < 0 ?
                            < p className="fst-italic text-danger fs-5">
                              {`Moins ${convertNumberToWords(Math.abs(supplierReport.summary.netApaye))} `}
                              Dirhams
                            </p> :
                            <p className="fst-italic text-success fs-5">
                              {`${convertNumberToWords(Math.abs(supplierReport.summary.netApaye))} `}
                              Dirhams
                            </p>
                        }
                        <p className="mt-3 small text-muted">
                          <FaCalendarAlt className="me-1" />
                          NB: Montant Total Consommé sur la période du <span className="fw-semibold">{format(parseDate(supplierReport.period.from), 'dd/MM/yyyy', { locale: fr })} au {format(parseDate(supplierReport.period.to), 'dd/MM/yyyy', { locale: fr })}</span>
                        </p>


                      </div>
                    </Col>
                  </Row>
                </div>

                <div className="report-footer mt-4 pt-3 border-top text-muted small">
                  <div className="d-flex justify-content-between align-items-center">
                    <span><FaCalendarAlt className="me-1" /> Document généré le: <span className="fw-semibold">{supplierReport.generationDate}</span></span>
                    <span className="fw-bold">Page 1</span>
                  </div>
                </div>
              </div>
            ) : (
              <div className="text-center p-5 bg-light rounded-4 border border-2 border-secondary animate__animated animate__fadeIn">
                {supplierReportLoading ? (
                  <div>
                    <Spinner color="primary" />
                    <p className="mt-3">Génération du rapport en cours...</p>
                  </div>
                ) : (
                  <div>
                    <FaFileAlt size={48} className="text-muted mb-3" />
                    <h5 className="fw-bold">Aucun rapport généré</h5>
                    <p className="text-muted">
                      Veuillez sélectionner un fournisseur et cliquer sur <span className="fw-semibold">"Générer le rapport"</span> pour afficher les détails des ventes.
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>
        </CardBody>
      </Card>

      <style jsx>{`
        .client-report {
          font-size: 0.9rem;
        }
        .summary-table td {
          padding: 0.4rem 0.75rem;
        }
        .company-info h5 {
          color: #2c3e50;
        }
        .amount-in-words {
          border-left: 3px solid #3498db;
        }
        @media print {
          .page-content {
            padding: 0 !important;
          }
          .client-report {
            border: none !important;
          }
        }
      `}</style>
    </Container>
  );
};

export default AchatsStatistiques;
